//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'

import { PoolTypeEnum, RoleEnum } from '@shared/Enums'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import React, { useEffect } from 'react'

import PoolSummary from '@react/components/pools/PoolSummary'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import { useNavigate } from 'react-router-dom'

import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Tab from '@mui/material/Tab'
import AdminGrantPoolAccessPage from '@react/admin/AdminGrantPoolAccessPage'
import { AdminLenderView } from '@react/admin/AdminLenderView'
import { PoolOffchainDataView } from '@react/admin/PoolOffchainDataView'
import { AdminDynamicWithdrawalsTab } from '@react/dynamic/AdminDynamicWithdrawalsTab'
import { DynamicDepositsTab } from '@react/dynamic/DynamicDepositsTab'
import LytPoolActionModals from '@react/lyt/LytPoolActionModals'
import LytPoolContractAddresses from '@react/lyt/LytPoolContractAddresses'
import LytPoolOverview from '@react/lyt/LytPoolOverview'

import LytExchangeRateCard from '@react/lyt/planning/LytExchangeRateCard'
import LytUpload from '@react/lyt/LytUpload'
import { logOT } from '@shared/loggingShared'
import { displayName } from '@shared/PoolBaseCTO'
enum TabEnum {
  OVERVIEW = 'OVERVIEW',
  ACCESS_LIST = 'ACCESS_LIST',
  MINTs = 'Mints',
  BURNS = 'Burns',
  MARKET_MAKER_VIEW = 'MARKET_MAKER_VIEW',
  LEGAL = 'LEGAL',
  CONTRACT_ADDRESSES = 'CONTRACT_ADDRESSES',
  OFFCHAIN_DATA = 'OFFCHAIN_DATA',
}
export default function LytIssuerPoolDetails(): React.ReactElement {
  const navigate = useNavigate()
  const {
    stageConfiguration: { lytPoolAddr },
  } = useOtChainState()

  const poolBaseOverviewCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[lytPoolAddr ?? ETH_ADDR_ZERO])

  const allPools = useOtSelector((state) => state.poolState.poolOverviewCTOs)

  const [selectedTab, setSelectedTab] = React.useState<TabEnum>(TabEnum.OVERVIEW)
  const handleChange = (_event: React.SyntheticEvent, newValue: TabEnum): void => {
    setSelectedTab(newValue)
  }
  useEffect(() => {
    if (lytPoolAddr && !poolBaseOverviewCTO && Object.keys(allPools).length > 0) {
      logOT.info('location navigate to /')
      navigate('/')
    }
  }, [lytPoolAddr, poolBaseOverviewCTO, allPools])

  logOT.info('lyt poolBaseOverviewCTO', lytPoolAddr, poolBaseOverviewCTO)
  if (poolBaseOverviewCTO && lytPoolAddr) {
    return (
      <Box>
        <LytExchangeRateCard />
        {poolBaseOverviewCTO?.poolType === PoolTypeEnum.Lyt && (
          <LytPoolActionModals poolAddr={lytPoolAddr} poolName={displayName(poolBaseOverviewCTO)} />
        )}

        <PoolSummary poolAddr={lytPoolAddr} />
        <Paper sx={{ marginTop: 4, marginBottom: 4 }}>
          <Box px={3} py={2}>
            <TabContext value={selectedTab}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList
                  onChange={handleChange}
                  aria-label="lab API tabs example"
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label={'Overview'} value={TabEnum.OVERVIEW} />
                  <Tab label={'Mints'} value={TabEnum.MINTs} />
                  <Tab label={'Burns'} value={TabEnum.BURNS} />
                  <Tab label={'Market Maker View'} value={TabEnum.MARKET_MAKER_VIEW} />
                  <Tab label={'OffChainData'} value={TabEnum.OFFCHAIN_DATA} />
                  <Tab label={'Market Maker List'} value={TabEnum.ACCESS_LIST} />
                  <Tab label={'Info'} value={TabEnum.LEGAL} />
                  <Tab label={'Contract Addresses'} value={TabEnum.CONTRACT_ADDRESSES} />
                </TabList>
              </Box>
              <TabPanel value={TabEnum.OVERVIEW}>
                <LytPoolOverview poolAddr={lytPoolAddr} />
              </TabPanel>

              <TabPanel value={TabEnum.MINTs}>
                <DynamicDepositsTab poolAddr={lytPoolAddr} />
              </TabPanel>

              <TabPanel value={TabEnum.BURNS}>
                <AdminDynamicWithdrawalsTab poolAddr={lytPoolAddr} />
              </TabPanel>

              <TabPanel value={TabEnum.MARKET_MAKER_VIEW}>
                <AdminLenderView poolAddr={lytPoolAddr} />
              </TabPanel>

              <TabPanel value={TabEnum.ACCESS_LIST}>
                <AdminGrantPoolAccessPage poolAddr={lytPoolAddr} />
              </TabPanel>
              <TabPanel value={TabEnum.OFFCHAIN_DATA}>
                <PoolOffchainDataView poolAddr={lytPoolAddr} />
              </TabPanel>
              <TabPanel value={TabEnum.LEGAL}>
                <LytUpload poolAddr={lytPoolAddr} />
              </TabPanel>
              <TabPanel value={TabEnum.CONTRACT_ADDRESSES}>
                <LytPoolContractAddresses role={RoleEnum.POOL_ADMIN} />
              </TabPanel>
            </TabContext>
          </Box>
        </Paper>
      </Box>
    )
  } else {
    return <></>
  }
}
